import { LoadingButton } from '@mui/lab';
import {
  Button,
  Card,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { useHubspotForms } from 'api/apiHooks';
import PartialPageSpinner from 'components/PartialPageSpinner';
import React, { useEffect, useImperativeHandle, useState } from 'react';
import SimpleBar from 'simplebar-react';
import { useTranslation } from 'react-i18next';
import { setSession } from 'utils/jwt';

export default React.forwardRef(function ChooseTemplate(
  {
    selectedFormId,
    setSelectedFormId,
    createFormInHubspot,
    creatingForm,
    nextStep,
    formCreationInProgress,
    userToken,
    setUserToken,
  }: {
    selectedFormId: number | null;
    setSelectedFormId: React.Dispatch<React.SetStateAction<number | null>>;
    createFormInHubspot: () => Promise<void>;
    creatingForm: boolean;
    nextStep: VoidFunction;
    formCreationInProgress: boolean;
    userToken: string | null;
    setUserToken: React.Dispatch<React.SetStateAction<string | null>>;
  },
  ref
) {
  const portalId = window.sessionStorage.getItem('portal_id');
  const {
    data: hubspotFormsRes,
    refetch,
    isFetching,
  } = useHubspotForms(portalId as unknown as string);
  const { t } = useTranslation();
  const [error, setError] = useState<null | string>(null);
  const [typeSelected, setTypeSelected] = useState('selectForm');
  const [disableFormCreation, setDisableFormCreation] = useState(false);
  setSession(userToken);
  const hubspotForms = hubspotFormsRes?.data;
  useImperativeHandle(ref, () => ({
    refetch,
  }));

  useEffect(() => {
    hubspotForms?.forEach((form: any) => {
      if (form.name === 'Test Onboarding Form') {
        setDisableFormCreation(true);
      } else {
        setDisableFormCreation(false);
      }
    });
  }, [hubspotForms]);

  const hsFormsDiv =
    userToken !== null ? (
      <>
        {isFetching ? (
          <>
            <div>
              <PartialPageSpinner />
            </div>
          </>
        ) : (
          <>
            {hubspotForms && hubspotForms.length > 0 ? (
              <>
                {hubspotForms?.map(hsForm => {
                  return (
                    <ListItem
                      sx={{
                        display: 'flex',
                        padding: '20px 10px',
                      }}
                      button
                      key={hsForm.guid}
                      className={`shadow-md shadow-slate-100 hover:bg-gray-200 rounded-2xl  mb-5 ${
                        selectedFormId === (hsForm.guid as unknown as number)
                          ? 'bg-[#7ac0f8]'
                          : ''
                      }`}
                    >
                      <ListItemText
                        onClick={() => {
                          setSelectedFormId(hsForm.guid as unknown as number);
                          nextStep();
                          setUserToken(userToken);
                          setSession(userToken);
                        }}
                        primary={hsForm.name}
                        secondary={
                          <>
                            <Typography
                              sx={{ display: 'inline' }}
                              component='span'
                              variant='body2'
                              color='text.secondary'
                            >
                              {hsForm.guid}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  );
                })}
              </>
            ) : hubspotForms && hubspotForms.length === 0 ? (
              <span className='min-h-[30vh] min-w-[30vw]'>
                {t('No_appropriate_forms_found_in_portal')}
              </span>
            ) : (
              <div>
                <PartialPageSpinner />
              </div>
            )}
          </>
        )}
      </>
    ) : (
      <></>
    );

  return (
    <div className='flex flex-col gap-2'>
      <h3 className='text-center'>{t('select_hubspot_form')}</h3>
      <span className='text-xs text-center'>
        {t('create_a_form_and_select')}
      </span>
      <div className='flex justify-end'>
        {disableFormCreation !== true ? (
          <LoadingButton
            className='mt-5'
            onClick={createFormInHubspot}
            loading={creatingForm}
          >
            + {t('create_a_form')}
          </LoadingButton>
        ) : (
          <Tooltip title='Form already created' placement='top-end' arrow>
            <span className='cursor-pointer'>
              <Button disabled> + {t('create_a_form')}</Button>
            </span>
          </Tooltip>
        )}
      </div>
      <Card className='p-5 min-w-[40vw]'>
        <SimpleBar className='max-h-[400px]'>
          <div>{hsFormsDiv}</div>
        </SimpleBar>
      </Card>

      {error && typeSelected === 'choose-form' && (
        <em className='text-red-500 text-sm ml-10'>{error}</em>
      )}
    </div>
  );
});
